import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { useBookingsContext } from '../../../../contexts/BookingsContext';
import { useEditableBlocksStyles } from './styles';
import { IUserDocumentSection, IUserDocumentTemplate } from '@homeporter/types';

import { TabType } from '../../../../global/interfaces';

interface GenerateReportProps {
  characterLimit: number;
  selectedTemplate: string;
  setSelectedTemplate: (value: string) => void;
  documentsRef: React.RefObject<IUserDocumentTemplate[]>;
  documentIndex: number;
  documentSection: IUserDocumentSection | null;
  setDocumentSection: (section: IUserDocumentSection | null) => void;
  setDocumentSections: (sections: IUserDocumentSection[]) => void;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  setSaveEnabled: (enabled: boolean) => void;
  setGenerateWholeDocument: (generate: boolean) => void;
}

const GenerateReport: React.FC<GenerateReportProps> = ({
  characterLimit,
  documentsRef,
  documentIndex,
  documentSection,
  setDocumentSection,
  activeTab,
  setActiveTab,
  setSaveEnabled,
  setGenerateWholeDocument,
}) => {
  const styles = useEditableBlocksStyles();
  const [initialContext, setInitialContext] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('custom');
  const { context } = useBookingsContext();
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  useEffect(() => {
    const currentTemplate =
      documentsRef.current?.[documentIndex]?.template_name;
    setTemplateName(currentTemplate || 'custom');
  }, [documentsRef.current?.[documentIndex]]);

  useEffect(() => {
    if (documentsRef.current?.[documentIndex]?.template_name) {
      setTemplateName(documentsRef.current[documentIndex].template_name);
    }
  }, [documentsRef.current?.[documentIndex]]);

  useEffect(() => {
    console.log(
      '::::::::useEffect setInitialContext: ',
      documentSection?.context || context || '',
    );
    if (!initialContext)
      setInitialContext(documentSection?.context || context || '');
  }, [documentSection]);

  const enableGenerateReport =
    initialContext.length > 3 &&
    (activeTab !== 'Cards' || documentSection?.cards.length);

  const handleWholeDocumentGeneration = async (initialContext: string) => {
    console.log('::::handleWholeDocumentGeneration...');
    if (!documentSection) return;

    if (documentSection?.cards.length === 0) {
      setActiveTab('Cards');
      setIsAlertOpen(true);
      return;
    }

    const updatedSection = { ...documentSection };
    updatedSection.context = initialContext;

    updatedSection.cards.forEach((card) => {
      if (card.type === 'Prompt') {
        card.modified = '';
        card.response = '';
        card.redo = [];
        card.undo = [];
        card.context = initialContext;
        card.auto = true;
      }
    });

    console.log('::::setDocument section 13...');
    setDocumentSection(updatedSection);
    setActiveTab('Cards');
    setSaveEnabled(true);

    setGenerateWholeDocument(true);
  };

  return (
    <>
      <Box
        marginTop={'1rem'}
        width={'100%'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        px={'2rem'}>
        <Typography fontSize={'1.2rem'} fontWeight={700}>
          New Section
        </Typography>
        <Button
          disabled={!enableGenerateReport}
          variant="contained"
          sx={{
            ...styles.report,
            opacity: !enableGenerateReport ? 0.25 : 1,
          }}
          onClick={(event) => {
            event.stopPropagation();
            handleWholeDocumentGeneration(initialContext);
          }}>
          Generate Report
        </Button>
      </Box>

      <Box px={'2rem'}>
        <Typography fontSize={'0.9rem'}>
          Please provide a summary of the session call in your own words. This
          will offer context to the AI, helping it generate the session report
          accurately. <br />
          <br />
          Additionally, please add prompt cards on the right side of the page to
          guide the AI on how to generate each subsection of the document.
        </Typography>
        <TextField
          sx={{
            marginTop: '1.5rem',
            '& .MuiOutlinedInput-root': {
              padding: '0',
            },
          }}
          label="Enter the description of the section context here."
          variant="outlined"
          value={initialContext}
          onChange={(event) => setInitialContext(event.target.value)}
          fullWidth
          multiline
          rows={6}
          inputProps={{ maxLength: characterLimit }}
        />
        <Typography variant="caption" color="textSecondary" sx={styles.context}>
          {initialContext.length} / {characterLimit}
        </Typography>
      </Box>

      <Dialog
        open={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        PaperProps={{
          sx: { borderRadius: '1rem', minWidth: '20rem' },
        }}>
        <DialogTitle>Action Required</DialogTitle>
        <DialogContent>
          Please add a prompt card before generating a report.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAlertOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateReport;
